/**
 * Iter 46 · Analista de Marketing — Novo Lead de Campanha
 *
 * Fluxo redesenhado: o Analista de MKT só informa dados básicos do lead + nome
 * e UF do município. O lead entra como PENDENTE de direcionamento — quem
 * escolhe qual vendedora vai receber é a Diretoria, na tela /leads-pendentes.
 */
import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Target, Sparkles, UserPlus, Building2, Clock, CheckCircle2 } from "lucide-react";

const CANAIS = [
  { v: "trafego_pago", label: "Tráfego Pago", icon: "📢" },
  { v: "email_mkt", label: "E-mail MKT", icon: "✉️" },
  { v: "site", label: "Site", icon: "🌐" },
  { v: "whatsapp", label: "WhatsApp", icon: "💬" },
  { v: "indicacao", label: "Indicação", icon: "🤝" },
  { v: "evento", label: "Evento", icon: "🎪" },
  { v: "outro", label: "Outro", icon: "📌" },
];

const MESES = [
  { v: 1, l: "Jan" }, { v: 2, l: "Fev" }, { v: 3, l: "Mar" }, { v: 4, l: "Abr" },
  { v: 5, l: "Mai" }, { v: 6, l: "Jun" }, { v: 7, l: "Jul" }, { v: 8, l: "Ago" },
  { v: 9, l: "Set" }, { v: 10, l: "Out" }, { v: 11, l: "Nov" }, { v: 12, l: "Dez" },
];
const UFS = [
  "AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI",
  "RJ","RN","RS","RO","RR","SC","SP","SE","TO",
];

const initialForm = {
  municipio_nome: "",
  uf: "",
  nome: "",
  funcao: "Vereador",
  whatsapp: "",
  email: "",
  canal: "trafego_pago",
  curso_interesse: "",
  observacoes: "",
};

export default function NovoLead() {
  const [form, setForm] = useState(initialForm);
  const [cursos, setCursos] = useState([]);
  const [filtroCursoAno, setFiltroCursoAno] = useState("");
  const [filtroCursoMes, setFiltroCursoMes] = useState("");
  const [filtroCursoUf, setFiltroCursoUf] = useState("");
  const [leadsRecentes, setLeadsRecentes] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadLeads = () => {
    api.get("/leads-mkt?limit=30")
      .then((r) => setLeadsRecentes(r.data || []))
      .catch(() => {});
  };

  useEffect(() => {
    api.get("/cursos").then((r) => setCursos(r.data || [])).catch(() => {});
    loadLeads();
  }, []);

  const cursosFiltrados = useMemo(() => {
    return (cursos || [])
      .filter((c) => {
        if (filtroCursoAno && !((c.mes_ano || "").startsWith(filtroCursoAno))) return false;
        if (filtroCursoMes) {
          const parts = (c.mes_ano || "").split("-");
          if (parts[1] !== String(filtroCursoMes).padStart(2, "0")) return false;
        }
        if (filtroCursoUf && (c.uf || "").toUpperCase() !== filtroCursoUf) return false;
        return true;
      })
      .sort((a, b) => (b.mes_ano || "").localeCompare(a.mes_ano || ""));
  }, [cursos, filtroCursoAno, filtroCursoMes, filtroCursoUf]);

  const anosDisponiveis = useMemo(() => {
    const anos = new Set();
    (cursos || []).forEach((c) => c.mes_ano && anos.add((c.mes_ano || "").split("-")[0]));
    const arr = [...anos].sort().reverse();
    return arr.length ? arr : [String(new Date().getFullYear())];
  }, [cursos]);

  const limparFiltrosCurso = () => {
    setFiltroCursoAno(""); setFiltroCursoMes(""); setFiltroCursoUf("");
  };

  const salvar = async () => {
    if (!form.municipio_nome.trim() || !form.uf) {
      toast.error("Informe o município e a UF");
      return;
    }
    if (!form.nome.trim()) {
      toast.error("Informe o nome do lead");
      return;
    }
    if (!form.canal) {
      toast.error("Selecione o canal de origem");
      return;
    }
    const payload = {
      municipio_nome: form.municipio_nome.trim(),
      uf: form.uf.trim().toUpperCase(),
      nome: form.nome.trim(),
      funcao: form.funcao,
      whatsapp: form.whatsapp || null,
      email: form.email || null,
      canal: form.canal,
      curso_interesse: form.curso_interesse && form.curso_interesse !== "__nao_especificado__" ? form.curso_interesse : null,
      observacoes: form.observacoes || null,
    };
    try {
      setSaving(true);
      await api.post("/leads-mkt", payload);
      toast.success(`✨ Lead registrado! A Diretoria vai direcionar para a vendedora certa.`);
      setForm(initialForm);
      loadLeads();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join(" · ") : (detail || "Erro ao registrar lead");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="novo-lead-page">
      <div>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900 flex items-center gap-2">
          <Target className="w-8 h-8 text-blue-600" />
          Novo Lead de Campanha
        </h1>
        <p className="text-slate-500 text-sm">
          Registre um lead vindo de campanha MKT. O lead entra na fila da Diretoria
          — quem escolhe a vendedora responsável é o Diretor.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* --------- FORM (2/3) --------- */}
        <Card className="p-6 rounded-2xl space-y-5 lg:col-span-2">
          {/* Município */}
          <section>
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 flex items-center gap-2">
              <Building2 className="w-4 h-4" /> Município do lead
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label>Município *</Label>
                <Input
                  value={form.municipio_nome}
                  onChange={(e) => setForm({ ...form, municipio_nome: e.target.value })}
                  placeholder="Ex.: Piratuba"
                  data-testid="in-mun-nome"
                />
              </div>
              <div>
                <Label>UF *</Label>
                <Select value={form.uf} onValueChange={(v) => setForm({ ...form, uf: v })}>
                  <SelectTrigger data-testid="in-mun-uf"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {UFS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-[11px] text-slate-500 italic mt-1">
              A Diretoria decide se este é um município novo ou vai casar com um já existente.
            </p>
          </section>

          {/* Pessoa */}
          <section className="pt-4 border-t border-slate-200">
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 flex items-center gap-2">
              <UserPlus className="w-4 h-4" /> Dados do lead
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Nome do lead *</Label>
                <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Ex.: João Silva" data-testid="in-lead-nome" />
              </div>
              <div>
                <Label>Cargo</Label>
                <Select value={form.funcao} onValueChange={(v) => setForm({ ...form, funcao: v })}>
                  <SelectTrigger data-testid="in-lead-funcao"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Vereador", "Presidente", "Prefeito", "Assessor", "Secretário", "Contador", "Servidor", "Outro"].map((f) => (
                      <SelectItem key={f} value={f}>{f}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>WhatsApp</Label>
                <Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="(11) 99999-9999" data-testid="in-lead-whatsapp" />
              </div>
              <div className="col-span-2">
                <Label>E-mail</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="in-lead-email" />
              </div>
            </div>
          </section>

          {/* Origem MKT */}
          <section className="pt-4 border-t border-slate-200">
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> Origem da campanha
            </div>
            <div>
              <Label>Canal *</Label>
              <div className="grid grid-cols-4 gap-2 mt-1">
                {CANAIS.map((c) => (
                  <button
                    key={c.v}
                    type="button"
                    onClick={() => setForm({ ...form, canal: c.v })}
                    className={`text-xs px-3 py-2 rounded-lg border-2 transition ${
                      form.canal === c.v
                        ? "border-blue-500 bg-blue-50 text-blue-800 font-bold"
                        : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                    }`}
                    data-testid={`btn-canal-${c.v}`}
                  >
                    <div className="text-lg">{c.icon}</div>
                    <div>{c.label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Curso de interesse com filtros ano/mês/UF (Iter 46) */}
            <div className="mt-4">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Label className="mb-0">Curso de interesse</Label>
                <select
                  value={filtroCursoAno}
                  onChange={(e) => setFiltroCursoAno(e.target.value)}
                  className="h-7 rounded-md border border-slate-200 bg-white px-1.5 text-xs"
                  data-testid="curso-filtro-ano"
                >
                  <option value="">Ano</option>
                  {anosDisponiveis.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
                <select
                  value={filtroCursoMes}
                  onChange={(e) => setFiltroCursoMes(e.target.value)}
                  className="h-7 rounded-md border border-slate-200 bg-white px-1.5 text-xs"
                  data-testid="curso-filtro-mes"
                >
                  <option value="">Mês</option>
                  {MESES.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                </select>
                <select
                  value={filtroCursoUf}
                  onChange={(e) => setFiltroCursoUf(e.target.value)}
                  className="h-7 rounded-md border border-slate-200 bg-white px-1.5 text-xs"
                  data-testid="curso-filtro-uf"
                >
                  <option value="">UF</option>
                  {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
                {(filtroCursoAno || filtroCursoMes || filtroCursoUf) && (
                  <button
                    type="button"
                    onClick={limparFiltrosCurso}
                    className="text-[11px] text-slate-500 hover:text-slate-800 font-bold"
                    data-testid="curso-filtros-limpar"
                  >Limpar</button>
                )}
              </div>
              <Select value={form.curso_interesse} onValueChange={(v) => setForm({ ...form, curso_interesse: v })}>
                <SelectTrigger data-testid="in-curso-interesse">
                  <SelectValue placeholder={`— Sem específico — (${cursosFiltrados.length} cursos)`} />
                </SelectTrigger>
                <SelectContent className="max-h-96">
                  <SelectItem value="__nao_especificado__">— Sem específico —</SelectItem>
                  {cursosFiltrados.map((c) => (
                    <SelectItem key={c.id} value={c.nome}>
                      {c.mes_ano ? `[${c.mes_ano}${c.uf ? ` ${c.uf}` : ""}] ` : ""}{c.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {cursosFiltrados.length === 0 && (
                <p className="text-[11px] text-amber-700 mt-1">Nenhum curso bate com esses filtros.</p>
              )}
            </div>

            <div className="mt-3">
              <Label>Observações</Label>
              <Input value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} placeholder="Ex.: Assistiu vídeo do Instagram" data-testid="in-observacoes" />
            </div>
          </section>

          <div className="pt-4 border-t border-slate-200 flex justify-between items-center gap-3">
            <div className="text-xs text-slate-500 italic">
              Após enviar, a Diretoria recebe uma notificação e direciona o lead para a vendedora certa.
            </div>
            <Button
              onClick={salvar}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="btn-salvar-lead"
            >
              <Target className="w-4 h-4 mr-1" />
              {saving ? "Enviando..." : "Enviar Lead"}
            </Button>
          </div>
        </Card>

        {/* --------- LEADS RECENTES (1/3) --------- */}
        <Card className="p-4 rounded-2xl">
          <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-3">
            📋 Meus leads recentes
          </div>
          <div className="space-y-2 max-h-[70vh] overflow-y-auto">
            {leadsRecentes.length === 0 && (
              <div className="text-xs text-slate-400 py-4 text-center">Nenhum lead direcionado ainda.</div>
            )}
            {leadsRecentes.map((l) => (
              <div key={l.id} className="border border-slate-200 rounded-lg p-2.5 hover:bg-slate-50" data-testid={`lead-recent-${l.id}`}>
                <div className="flex items-start justify-between gap-1">
                  <div className="text-sm font-bold text-slate-900 truncate">{l.nome}</div>
                  <span className="text-[10px] text-slate-400 whitespace-nowrap">
                    {l.origem_lead_em ? new Date(l.origem_lead_em).toLocaleDateString("pt-BR") : ""}
                  </span>
                </div>
                <div className="text-xs text-slate-600">
                  {l.funcao} · {l.municipio_nome || l.origem_lead_municipio_nome} · {l.municipio_uf || l.origem_lead_uf}
                </div>
                <div className="mt-1 flex flex-wrap gap-1 text-[10px]">
                  <span className="bg-blue-100 text-blue-700 rounded px-1.5 py-0.5 font-bold">
                    {CANAIS.find((c) => c.v === l.origem_lead_canal)?.label || l.origem_lead_canal}
                  </span>
                  {l.origem_lead_status === "direcionado" ? (
                    <span className="bg-emerald-100 text-emerald-700 rounded px-1.5 py-0.5 font-bold inline-flex items-center gap-0.5">
                      <CheckCircle2 className="w-3 h-3" /> Direcionado
                    </span>
                  ) : (
                    <span className="bg-amber-100 text-amber-800 rounded px-1.5 py-0.5 font-bold inline-flex items-center gap-0.5">
                      <Clock className="w-3 h-3" /> Aguardando
                    </span>
                  )}
                  {l.origem_lead_curso_interesse && (
                    <span className="bg-amber-100 text-amber-700 rounded px-1.5 py-0.5">
                      {l.origem_lead_curso_interesse}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
